<template>
  <div v-if="show" class="tt-modal-root" :class="wrapClassName">
    <transition name="transition-fade">
      <div
        v-if="mask"
        v-show="showMask"
        :style="maskStyle"
        class="tt-modal-mask"
      />
    </transition>
    <div
      class="tt-modal-wrap"
      :class="{ 'tt-modal--centered': center }"
      @click="wrapClick"
    >
      <transition
        name="transition-zoom"
        appear
        @after-enter="enter"
        @after-leave="leave"
      >
        <div
          v-show="showModal"
          ref="modal"
          :key="key"
          class="tt-modal"
          :class="className"
          :style="wStyle"
        >
          <div
            v-if="$slots.header || title || closable"
            class="tt-modal-header"
          >
            <slot v-if="$slots.header || title" name="header">
              <h2 class="tt-modal-title">
                {{ title }}
              </h2>
            </slot>
            <Icon
              v-if="closable"
              name="cross"
              class="tt-modal-close"
              aria-label="close"
              role="button"
              @click.native.stop="cancel"
            />
          </div>
          <div class="tt-modal-body" :style="bodyStyle">
            <slot />
          </div>
          <div v-if="footer || $slots.footer" class="tt-modal-footer">
            <slot name="footer">
              <Button
                v-if="okButton"
                theme="primary"
                class="tt-modal-button"
                :loading="confirmLoading"
                @click="confirm"
              >
                {{ okBtnText }}
              </Button>
              <Button
                v-if="cancelButton"
                class="tt-modal-button"
                @click="cancel"
              >
                {{ cancelBtnText }}
              </Button>
            </slot>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
import Vue from 'vue'
import Icon from 'com/Icon'
import Button from 'com/Button'
import throttle from 'lodash/throttle'

const bodyLockClass = 'tt-modal--body-lock'
export default {
  name: 'Modal',
  components: { Icon, Button },
  props: {
    visible: Boolean,
    title: String,
    width: Number,
    mask: {
      type: Boolean,
      default: true
    },
    footer: {
      type: Boolean,
      default: true
    },
    closable: Boolean,
    // 点击外部关闭弹窗
    maskClosable: {
      type: Boolean,
      default: true
    },
    // esc 按钮关闭弹窗
    escClosable: {
      type: Boolean,
      default: true
    },
    center: {
      type: Boolean,
      default: true
    },
    // 点击确认按钮后是否自动关闭弹窗
    closeAfterConfirm: {
      type: Boolean,
      default: true
    },
    // 是否挂载到 body 上
    appendToBody: Boolean,
    // 打开弹窗后 body 标签不滚动
    lockBodyScroll: Boolean,
    // 关闭后销毁元素
    destroyOnClose: Boolean,
    okText: String,
    cancelText: String,
    okButton: {
      type: Boolean,
      default: true
    },
    cancelButton: {
      type: Boolean,
      default: true
    },
    confirmLoading: Boolean,
    className: String | Object | Array,
    wrapClassName: String | Object | Array,
    wrapStyle: Object,
    bodyStyle: Object,
    maskStyle: Object
  },
  model: {
    prop: 'visible',
    event: 'change'
  },
  watch: {
    visible(val) {
      val ? this.open() : this.close()
    }
  },
  data() {
    return {
      show: false,
      showMask: false,
      showModal: false,
      key: 0
    }
  },
  computed: {
    okBtnText() {
      return this.okText || Vue.t('confirm')
    },
    cancelBtnText() {
      return this.cancelText || Vue.t('cancel')
    },
    wStyle() {
      const bodyStyle = this.bodyStyle || {}
      if (this.width) {
        bodyStyle.width = this.width + 'px'
      }
      return bodyStyle
    }
  },
  mounted() {
    if (this.visible) this.open()
    if (this.appendToBody) document.body.appendChild(this.$el)
  },
  beforeDestroy() {
    this.removeBodyEl()
    window.removeEventListener('keyup', this.keyup)
  },
  methods: {
    enter() {
      this.$emit('open')
      this.$emit('change', true)
    },
    leave() {
      this.show = false
      this.$emit('close')
      this.$emit('change', false)
      if (this.destroyOnClose) this.key++
      this.removeBodyEl()
    },
    close() {
      if (this.escClosable) window.removeEventListener('keyup', this.keyup)
      if (this.lockBodyScroll) document.body.classList.remove(bodyLockClass)
      this.$emit('before-close')
      this.showMask = false
      this.showModal = false
    },
    open() {
      // 添加 ESC 按键处理
      if (this.escClosable) window.addEventListener('keyup', this.keyup)
      // 弹窗后锁定body滚动
      if (this.lockBodyScroll) document.body.classList.add(bodyLockClass)
      this.$emit('before-open')
      this.show = true
      this.$nextTick(() => {
        if (this.appendToBody) {
          document.body.appendChild(this.$el)
        }
        this.showMask = true
        this.showModal = true
      })
    },
    confirm: throttle(
      function() {
        this.$emit('confirm')
        if (this.closeAfterConfirm) this.close()
      },
      1000,
      { trailing: false }
    ),
    cancel: throttle(
      function() {
        this.$emit('cancel')
        this.close()
      },
      1000,
      { trailing: false }
    ),
    keyup(evt) {
      if (evt.which === 27 && this.show) {
        this.cancel()
      }
    },
    wrapClick(evt) {
      const $modal = this.$refs.modal
      const target = evt.target
      if (!$modal || target === $modal || $modal.contains(target)) return
      if (this.maskClosable) this.cancel()
    },
    removeBodyEl() {
      if (this.appendToBody && document.body.contains(this.$el)) {
        document.body.removeChild(this.$el)
      }
    }
  }
}
</script>
<style lang="less">
@import '~uikit';
@prefix-cls: tt-modal;
.@{prefix-cls} {
  position: relative;
  width: 540px;
  margin: 0 auto;
  background-color: var(--bg-block);
  background-clip: padding-box;
  border: 0;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12);
  pointer-events: auto;
  &--body-lock {
    overflow: hidden;
  }
  &--centered {
    text-align: center;
    &:before {
      display: inline-block;
      width: 0;
      height: 100%;
      vertical-align: middle;
      content: '';
    }
    .@{prefix-cls} {
      top: 0;
      display: inline-block;
      text-align: initial;
      vertical-align: middle;
    }
  }
  &-wrap {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1040;
    overflow: auto;
    outline: 0;
    -webkit-overflow-scrolling: touch;
  }
  &-mask {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1040;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
  }
  &-title {
    margin: 0;
    padding: 15px 20px 0;
    font-size: 16px;
    text-align: center;
    color: var(--active-color);
  }
  &-body {
    padding: 20px;
  }
  &-close {
    position: absolute;
    right: 15px;
    top: 15px;
    font-size: 10px;
    color: var(--sub-text-color);
    cursor: pointer;
  }
  &-footer {
    padding: 10px 20px 20px;
    text-align: center;
  }
  &-button {
    width: 100px;
    height: 30px;
    padding: 0;
    margin: 0 15px 0 0;
    outline: none;
    border: none;
    border-radius: 2px;
    line-height: 30px;
    &:last-child {
      margin-right: 0;
    }
  }
}
</style>
